"use client";

import { useState } from "react";
import { Image } from "../../elements/image/image";
import { Text } from "../../elements/text/text";
import { CardGeneric } from "../../patterns/card-generic/card-generic";
import { Modal } from "../modal/modal";

// Module-level constants to avoid recreating on every render
const MAX_CARDS_TO_DISPLAY = 3;

export type ProductHighlight = {
  code?: string;
  title?: string;
  teaser?: string;
  content?: string;
  picture?: {
    code?: string;
    mime?: string;
    title?: string;
    url?: string;
    watermarked?: boolean;
  };
};

export interface ProductHighlightsProps {
  highlights: ProductHighlight[];
  maxItems?: number;
  microcopies: {
    learnMore: string;
    title?: string;
    subtitle?: string;
  };
}

export function ProductHighlights(props: Readonly<ProductHighlightsProps>) {
  const [selectedHighlight, setSelectedHighlight] =
    useState<ProductHighlight | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const learnMoreText = props.microcopies.learnMore;
  const titleText = props.microcopies?.title || "Product Highlights";
  const subtitleText = props.microcopies?.subtitle || "";

  // We rely on CSS line-clamp to truncate descriptions with ellipses.

  const handleLearnMore = (highlight: ProductHighlight) => {
    setSelectedHighlight(highlight);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedHighlight(null);
  };

  // Filter highlights to only include those with required fields (title)
  const validHighlights =
    props.highlights?.filter(
      (highlight) => highlight.title && highlight.code
    ) ?? [];

  if (validHighlights.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mb-5" data-testid="product-highlights-header">
        <Text variant="h2">{titleText}</Text>
        {subtitleText ? (
          <div className="mt-2.5">
            <Text color="muted" variant="body-sm">
              {subtitleText}
            </Text>
          </div>
        ) : null}
      </div>

      {/*
        Figma ".Slider part / Stack": one horizontal row of fixed-width cards
        (18.125rem/290px on mobile, 24.4375rem/391px from `sm` up) with an 8px
        gap, clipped to the section. Cards keep their width at every
        breakpoint and overflow into a
        swipe rather than reflowing into a grid, so the section stays the same
        height on a phone as on a 2560 screen.
      */}
      <div
        className="flex min-w-0 snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]"
        data-testid="product-highlights-slider"
      >
        {validHighlights
          .slice(0, props.maxItems ?? MAX_CARDS_TO_DISPLAY)
          .map((highlight) => (
            <div
              className="w-[18.125rem] shrink-0 snap-start sm:w-[24.4375rem]"
              key={highlight.code}
            >
              <CardGeneric
                button={{
                  text: learnMoreText,
                  href: "#",
                  onClick: () => {
                    handleLearnMore(highlight);
                  },
                }}
                description={highlight.teaser || ""}
                key={highlight.code}
                overlayImage={{
                  url: highlight.picture?.url || "",
                  alt:
                    highlight.picture?.title ||
                    highlight.title ||
                    "Product Highlight",
                }}
                title={highlight.title || ""}
                variant="secondary-image-full"
              />
            </div>
          ))}
      </div>

      {/* Product Highlights Modal */}
      <Modal
        isOpen={isModalOpen}
        modalClassName="max-w-2xl md:max-w-4xl"
        onClose={handleCloseModal}
        title={selectedHighlight?.title || ""}
      >
        {selectedHighlight && (
          <div className="w-full space-y-4">
            {/* Image Section - Full width fill */}
            {selectedHighlight.picture?.url && (
              <div className="w-full overflow-hidden rounded-sm bg-neutral-100">
                <Image
                  alt={
                    selectedHighlight.picture.title ||
                    selectedHighlight.title ||
                    "Product Highlight"
                  }
                  aspectRatio="16:9"
                  className="block h-auto w-full"
                  loading="lazy"
                  objectPosition="center"
                  placeholder="empty"
                  src={selectedHighlight.picture.url}
                />
              </div>
            )}

            {/* Content Section (prefer content, fallback to teaser) */}
            {(selectedHighlight.content || selectedHighlight.teaser) && (
              <div className="w-full">
                <Text color="muted" variant="body-md">
                  {selectedHighlight.content || selectedHighlight.teaser}
                </Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
